import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CalendarDays, Copy, SearchX, ShieldPlus, Truck, Undo2 } from 'lucide-react';
import {
  Alert,
  Button,
  EmptyState,
  Icon,
  KeyValueList,
  KeyValueRow,
  PageHeader,
  ProductThumb,
  StatusPill,
  Timeline,
  WhatsAppIcon,
} from '@/components/common';
import { useCustomerOrders } from '@/hooks/useCustomerProducts';
import { whatsappUrl } from '@/lib/support';
import type { ProductCase } from '@/types/product';
import { addProtectionNotLive, comingSoon } from '@/utils/orderPresentation';
import { formatDay, formatRupees } from '@/utils/productCases';
import { formatEventTime, SHIPMENT_BADGE, shipmentSteps, shipmentView } from '@/utils/shipment';

const card = 'tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5';

const copyAwb = async (awb: string) => {
  try {
    await navigator.clipboard.writeText(awb);
    toast.success('Tracking number copied');
  } catch {
    toast.error('Couldn’t copy the tracking number');
  }
};

/** S1 / S3: expected delivery date and the courier with its tracking number. */
const EtaCard: React.FC<{ order: ProductCase; shipped: boolean }> = ({ order, shipped }) => {
  const { eta, etaNote, courier, awb } = order.shipment ?? {};
  const note = shipped ? etaNote : `${order.merchant ?? 'The store'} hasn’t shipped this order yet.`;
  return (
    <section aria-label="Delivery estimate" className={`${card} tw-gap-4`}>
      {eta && (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{shipped ? 'Arriving' : 'Expected by'}</p>
          <p className="tw-text-[28px] tw-font-bold tw-leading-9 tw-tracking-[-0.3px] tw-text-ds-text-primary">{eta}</p>
          {note && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{note}</p>}
        </div>
      )}
      {shipped && courier && (
        <div className={`tw-flex tw-items-center tw-gap-3 ${eta ? 'tw-border-t tw-border-ds-border-default tw-pt-4' : ''}`}>
          <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-subtle tw-text-ds-text-secondary">
            <Icon icon={Truck} size={18} />
          </span>
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
            <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{courier}</p>
            {awb && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">AWB {awb}</p>}
          </div>
          {awb && (
            <button
              type="button"
              aria-label="Copy tracking number"
              onClick={() => copyAwb(awb)}
              className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-text-ds-text-secondary hover:tw-bg-ds-subtle hover:tw-text-ds-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
            >
              <Icon icon={Copy} size={18} />
            </button>
          )}
        </div>
      )}
    </section>
  );
};

/** S1–S4 Track shipment / Shipment details for one order. */
const ShipmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { orders, loading, error, reload } = useCustomerOrders({ withShipmentSamples: true });
  const order = orders.find((o) => o.orderId === id);
  const back = { to: `/customer/orders/${id}`, label: 'Order details' };

  if (loading || error || !order) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <PageHeader title="Track shipment" back={back} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Order not found"
            description="This order isn’t linked to your mobile number."
            action={<Button onClick={() => navigate('/customer/orders')}>Back to orders</Button>}
          />
        )}
      </div>
    );
  }

  const view = shipmentView(order);
  const { tone, label } = SHIPMENT_BADGE[view];
  const delivered = view === 'delivered';
  const merchant = order.merchant ?? 'the store';
  // The designs offer protection here only while in transit (S1) and after delivery (S4).
  const canProtect = order.state !== 'protected' && !order.protectionClosedOn && (view === 'in_transit' || delivered);
  const ret = order.returnAction;

  const summary: KeyValueRow[] = [
    order.orderLabel && { label: 'Order ID', value: order.orderLabel },
    order.deliverTo && { label: delivered ? 'Delivered to' : 'Deliver to', value: order.deliverTo },
  ].filter(Boolean) as KeyValueRow[];

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        title={delivered ? 'Shipment details' : 'Track shipment'}
        badge={<StatusPill tone={tone}>{label}</StatusPill>}
        subtitle={[order.title, order.orderLabel].filter(Boolean).join(' · ')}
        back={back}
      />

      <div className="tw-grid tw-items-start tw-gap-5 lg:tw-grid-cols-[minmax(0,600fr)_minmax(0,456fr)] lg:tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-5">
          {view === 'failed' && (
            <>
              <Alert tone="warning" title="Delivery attempt failed">
                {order.shipment?.failedAttempt?.message}
              </Alert>
              <div className="tw-flex tw-flex-col tw-gap-2.5 sm:tw-flex-row">
                <Button leadingIcon={CalendarDays} className="sm:tw-flex-1" onClick={() => comingSoon('Rescheduling a delivery')}>
                  Reschedule delivery
                </Button>
                <Button
                  variant="secondary"
                  leadingIcon={WhatsAppIcon}
                  className="sm:tw-flex-1"
                  href={whatsappUrl(`Hi, my delivery for order ${order.orderLabel ?? order.title} failed. Can you help?`)}
                  target="_blank"
                >
                  Chat with support
                </Button>
              </div>
            </>
          )}
          {delivered && order.deliveredAt && (
            <Alert tone="success" title={`Delivered on ${formatEventTime(order.deliveredAt)}`}>
              {order.shipment?.receivedBy ?? `Delivered by ${order.shipment?.courier ?? merchant}.`}
            </Alert>
          )}
          {(view === 'in_transit' || view === 'not_shipped') && (order.shipment?.eta || order.shipment?.courier) && (
            <EtaCard order={order} shipped={view === 'in_transit'} />
          )}
          {view === 'not_shipped' && (
            <Alert title="Tracking details on the way">
              The courier and tracking number appear here as soon as {merchant} ships your order.
            </Alert>
          )}

          <section aria-labelledby="shipment-progress" className={`${card} tw-gap-[14px]`}>
            <h2 id="shipment-progress" className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">
              Shipment progress
            </h2>
            <Timeline steps={shipmentSteps(order)} />
          </section>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-5">
          <section aria-label="Order summary" className={`${card} tw-gap-1`}>
            <div className="tw-flex tw-items-center tw-gap-[14px] tw-pb-2">
              <ProductThumb src={order.imageUrl} alt={order.title} />
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
                <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{order.title}</p>
                <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                  {[order.merchant, formatRupees(order.price)].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            {summary.length > 0 && <KeyValueList rows={summary} />}
          </section>

          {canProtect && (
            <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-bg-ds-brand-tint tw-p-4">
              <div className="tw-flex tw-flex-col tw-gap-1">
                <h2 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
                  {delivered ? `Protect your ${order.title}` : 'Protect it before it arrives'}
                </h2>
                <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                  {delivered
                    ? order.protectionDeadline
                      ? `Add a plan until ${formatDay(order.protectionDeadline)}.`
                      : 'Add Accidental Damage or Extended Warranty cover.'
                    : 'Add Accidental Damage cover now — it starts today.'}
                </p>
              </div>
              <Button size="sm" leadingIcon={ShieldPlus} fullWidth onClick={addProtectionNotLive}>
                Add protection
              </Button>
            </section>
          )}

          {delivered && ret && !ret.disabled && (
            <section className={`${card} tw-gap-3 tw-p-4`}>
              <h2 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
                {order.returnWindow?.endsAt ? `Return window open till ${formatDay(order.returnWindow.endsAt).slice(0, 6)}` : 'Return window open'}
              </h2>
              <Button size="sm" variant="secondary" leadingIcon={Undo2} fullWidth onClick={() => ret.path && navigate(ret.path)}>
                Return item
              </Button>
            </section>
          )}

          {view === 'failed' && (
            <Alert>After 3 failed attempts, the courier returns the order to {merchant} and your refund is processed automatically.</Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipmentPage;
